import { Link, usePage } from '@inertiajs/react'
import AppLayout from '@/layouts/AppLayout'
import { PageContainer } from '@/components/PageContainer'
import { PageHeader } from '@/components/PageHeader'
import { FlashBanner } from '@/components/FlashBanner'
import { ScreenGuideDialog } from '@/components/ScreenGuideDialog'
import { Button } from '@/components/ui/button'
import { useScreenGuide } from '@/hooks/useScreenGuide'
import { formatDate } from '@/lib/date'
import type { ProjectSummary } from '@/types/project'
import type { SharedData } from '@/types/shared'
import { PlusIcon, CircleHelpIcon } from 'lucide-react'
import { ProjectCard } from './components/ProjectCard'
import { SpecBadge } from './components/SpecBadge'
import { onboardingContent } from './onboardingContent'
import { OnboardingDiagram } from './OnboardingDiagram'

interface Props {
  projects: ProjectSummary[]
}

export default function ProjectsIndex ({ projects }: Props): React.ReactElement {
  const { current_user: currentUser } = usePage<SharedData>().props
  const guide = useScreenGuide(
    'projects',
    currentUser?.onboardings ?? {},
    currentUser?.account_type === 'demo'
  )
  const guideSections = onboardingContent.sections.map((section, i) =>
    i === 0 ? { ...section, graphic: <OnboardingDiagram /> } : section
  )

  return (
    <AppLayout>
      <PageContainer>
        <PageHeader title='プロジェクト' subtitle='参加しているプロジェクト一覧'>
          <Button type='button' variant='ghost' size='sm' onClick={() => { guide.openHelp() }}>
            <CircleHelpIcon size={14} /> 使い方
          </Button>
          <Button asChild>
            <Link href='/projects/new'>
              <PlusIcon width={14} height={14} />
              プロジェクトを作成
            </Link>
          </Button>
        </PageHeader>

        <ScreenGuideDialog
          open={guide.open}
          onClose={() => { guide.close() }}
          title={onboardingContent.title}
          sections={guideSections}
        />

        <FlashBanner />

        {projects.length === 0 && (
          <div className='text-center py-15 px-5 text-(--bp-ink3) text-sm'>
            <div className='text-[32px] mb-3'>📋</div>
            <div className='font-semibold mb-1.5 text-(--bp-ink)'>プロジェクトがありません</div>
            <div>最初のプロジェクトを作成して要件定義を始めましょう</div>
          </div>
        )}

        {projects.map((p) => (
          <ProjectCard
            key={p.id}
            href={`/projects/${p.id}`}
            initial={p.name[0].toUpperCase()}
            name={p.name}
            meta={
              <>
                {(p.industry != null) && <span>{p.industry?.label}</span>}
                {(p.industry != null) && <span>·</span>}
                <SpecBadge rd={p.requirements_specification} />
                <span>·</span>
                <span>最終更新 {formatDate(p.updated_at)}</span>
              </>
            }
          />
        ))}
      </PageContainer>
    </AppLayout>
  )
}
